import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api } from '../../services/api';
import {
  RefreshCw, Loader2, Brain, Plus, Trash2, Save,
  X, ToggleLeft, ToggleRight, Play, Upload, AlertTriangle, DollarSign, Info,
  FileText, Copy, Check,
} from 'lucide-react';

// ─── Prompt Name Mapping ────────────────────────────────
const FUNCTION_TO_PROMPT: Record<string, string> = {
  'gen_contact_ai_select': 'gen_contact',
  'gen_summary_ai_generate': 'gen_summary',
  'gen_skills_ai_select': 'gen_skills',
  'gen_highlights_ai_generate': 'gen_highlights',
  'gen_experience_ai_select': 'gen_experience',
  'gen_experience_ai_generate': 'gen_experience_generate',
  'gen_education_ai_select': 'gen_education',
  'gen_awards_ai_select': 'gen_awards',
  'gen_presentations_ai_select': 'gen_presentations',
  'gen_publications_ai_select': 'gen_publications',
  'gen_portfolios_ai_select': 'gen_portfolios',
  'gen_coverletter_ai_generate': 'gen_coverletter',
  'gen_tags_resume': 'gen_tags_resume',
  'gen_tags_jobdesc': 'gen_tags_jobdesc',
  'import_pdf_to_resume': 'import_resume',
  'import_txt_to_resume': 'import_resume',
  'import_validate_resume': 'import_validate_resume',
  'validate_resume': 'validate_resume',
  'validate_jobdesc': 'validate_jobdesc',
};

// ─── Common Prompt Parameters Reference ─────────────────
const PROMPT_PARAMETERS: { name: string; description: string }[] = [
  { name: '{{RESUME}}', description: 'Full resume JSON data' },
  { name: '{{RESUME_SECTION}}', description: 'Single resume section data (e.g., skills, education)' },
  { name: '{{JOB_DESC}}', description: 'Full job description text' },
  { name: '{{RESUME_TAGS}}', description: 'Pre-computed resume tags (comma-separated skills, domains, themes)' },
  { name: '{{JOB_TAGS}}', description: 'Pre-computed job description tags (comma-separated requirements, skills)' },
  { name: '{{RESUME_INPUT}}', description: 'Raw text/PDF content for resume import parsing' },
  { name: '{{VALIDATION_ERRORS}}', description: 'List of validation errors found during data check (JSON)' },
  { name: '{{VALIDATION_DATA}}', description: 'The data payload being validated (JSON)' },
];

// Types
interface AIModel {
  model_id: string;
  display_name: string;
  enabled: boolean;
  input_cost_per_1k?: number | null;
  output_cost_per_1k?: number | null;
}

interface ModelAssignment {
  function_id: string;
  model_id: string;
  description: string;
  ai_pct?: number;
  token_in_formula?: string;
  token_out_formula?: string;
}

interface CostConfig {
  job_desc: number;
  resume: number;
  small_section: number;
  experience_section: number;
  resume_tags: number;
  job_tags: number;
  jobs_per_month: number;
}

interface ModelPricingEntry {
  model_id: string;
  input_cost_per_1k: number | null;
  output_cost_per_1k: number | null;
}

const DEFAULT_COST_CONFIG: CostConfig = {
  job_desc: 1500,
  resume: 3000,
  small_section: 120,
  experience_section: 1000,
  resume_tags: 400,
  job_tags: 400,
  jobs_per_month: 100,
};

// ─── Helper: resolve variable formula to token count ────────
function resolveFormula(formula: string | undefined, vars: CostConfig): number {
  if (!formula) return 0;
  // Replace {variable} references with their values, then sum the parts
  let resolved = formula;
  resolved = resolved.replace(/\{job_desc\}/g, String(vars.job_desc));
  resolved = resolved.replace(/\{resume\}/g, String(vars.resume));
  resolved = resolved.replace(/\{small_section\}/g, String(vars.small_section));
  resolved = resolved.replace(/\{experience_section\}/g, String(vars.experience_section));
  resolved = resolved.replace(/\{resume_tags\}/g, String(vars.resume_tags));
  resolved = resolved.replace(/\{job_tags\}/g, String(vars.job_tags));
  // Evaluate simple addition: "1500 + 120" → 1620
  try {
    const parts = resolved.split('+').map((p) => parseFloat(p.trim())).filter((n) => !isNaN(n));
    return parts.reduce((sum, n) => sum + n, 0);
  } catch {
    return 0;
  }
}

// ─── Helper: calculate cost per request for a function ──────
function calcCostPerRequest(
  assignment: ModelAssignment,
  costConfig: CostConfig,
  pricing: Record<string, ModelPricingEntry>,
): number {
  const aiPct = (assignment.ai_pct ?? 0) / 100;
  const tokenIn = resolveFormula(assignment.token_in_formula, costConfig);
  const tokenOut = resolveFormula(assignment.token_out_formula, costConfig);
  const modelPricing = pricing[assignment.model_id];
  if (!modelPricing) return 0;
  const inCost = modelPricing.input_cost_per_1k ?? 0;
  const outCost = modelPricing.output_cost_per_1k ?? 0;
  return (tokenIn / 1000) * inCost * aiPct + (tokenOut / 1000) * outCost * aiPct;
}

// ─── Main Model Management Page ──────────────────────────
export function ModelManagement() {
  const [models, setModels] = useState<AIModel[]>([]);
  const [assignments, setAssignments] = useState<ModelAssignment[]>([]);
  const [costConfig, setCostConfig] = useState<CostConfig>(DEFAULT_COST_CONFIG);
  const [pricing, setPricing] = useState<Record<string, ModelPricingEntry>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);

  // Add model form state
  const [searchQuery, setSearchQuery] = useState('');
  const [addingModel, setAddingModel] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<Array<{ model_id: string; model_name: string; provider: string; input_cost_per_1k: number | null; output_cost_per_1k: number | null }>>([]);
  const [searching, setSearching] = useState(false);

  // Edit model state
  const [editingModel, setEditingModel] = useState<string | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');

  // Test model state
  const [testingModel, setTestingModel] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ modelId: string; response: string } | null>(null);

  // Assignment save state
  const [savingAssignment, setSavingAssignment] = useState<string | null>(null);
  // Cost config save state
  const [savingCostConfig, setSavingCostConfig] = useState(false);
  // Inline edit state for function cost fields
  const [editingField, setEditingField] = useState<{ functionId: string; field: string } | null>(null);
  const [editFieldValue, setEditFieldValue] = useState('');
  // Refresh pricing state
  const [refreshingPricing, setRefreshingPricing] = useState(false);
  const [pricingDebug, setPricingDebug] = useState<{
    unmatched_models?: Array<{ model_id: string; display_name: string }>;
    available_pricing_names?: string[];
  } | null>(null);

  // ─── Prompt Editor State ─────────────────────────────────
  const [promptEditorOpen, setPromptEditorOpen] = useState(false);
  const [selectedPromptName, setSelectedPromptName] = useState('');
  const [promptVersions, setPromptVersions] = useState<any[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<any>(null);
  const [flashNewVersion, setFlashNewVersion] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<any>(null);
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [autoIncrementPatch, setAutoIncrementPatch] = useState(() =>
    localStorage.getItem('admin_auto_increment_patch') !== 'false'
  );
  const [promptFilter, setPromptFilter] = useState({ type: '', state: '' });

  // ─── Snippets State ───────────────────────────────────
  const [snippets, setSnippets] = useState<{ prompt_name: string; prompt_version: string; prompt_state: string }[]>([]);

  // ─── Clipboard Copy State ─────────────────────────────
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 1500);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [modelsResult, assignmentsResult] = await Promise.all([
        api.listModels(),
        api.listModelAssignments(),
      ]);
      const loadedModels = (modelsResult.models || []) as AIModel[];
      setModels(loadedModels);

      // Separate _cost_config from function assignments
      const allItems = assignmentsResult.functions || [];
      const configItem = allItems.find((f) => f.function_id === '_cost_config');
      const funcItems = allItems.filter((f) => f.function_id !== '_cost_config');
      setAssignments(funcItems as ModelAssignment[]);

      if (configItem) {
        setCostConfig({
          job_desc: Number(configItem.job_desc ?? DEFAULT_COST_CONFIG.job_desc),
          resume: Number(configItem.resume ?? DEFAULT_COST_CONFIG.resume),
          small_section: Number(configItem.small_section ?? DEFAULT_COST_CONFIG.small_section),
          experience_section: Number(configItem.experience_section ?? DEFAULT_COST_CONFIG.experience_section),
          resume_tags: Number(configItem.resume_tags ?? DEFAULT_COST_CONFIG.resume_tags),
          job_tags: Number(configItem.job_tags ?? DEFAULT_COST_CONFIG.job_tags),
          jobs_per_month: Number(configItem.jobs_per_month ?? DEFAULT_COST_CONFIG.jobs_per_month),
        });
      }

      // Fetch API pricing separately (non-blocking), then merge with stored pricing
      try {
        const pricingResult = await api.getModelPricing();
        const apiPricing = pricingResult.pricing || {};
        // Merge: stored model pricing takes precedence over API pricing
        const merged: Record<string, ModelPricingEntry> = { ...apiPricing };
        for (const m of loadedModels) {
          if (m.input_cost_per_1k != null || m.output_cost_per_1k != null) {
            merged[m.model_id] = {
              model_id: m.model_id,
              input_cost_per_1k: m.input_cost_per_1k ?? apiPricing[m.model_id]?.input_cost_per_1k ?? null,
              output_cost_per_1k: m.output_cost_per_1k ?? apiPricing[m.model_id]?.output_cost_per_1k ?? null,
            };
          }
        }
        setPricing(merged);
      } catch {
        // Fall back to stored pricing only
        const stored: Record<string, ModelPricingEntry> = {};
        for (const m of loadedModels) {
          if (m.input_cost_per_1k != null || m.output_cost_per_1k != null) {
            stored[m.model_id] = { model_id: m.model_id, input_cost_per_1k: m.input_cost_per_1k ?? null, output_cost_per_1k: m.output_cost_per_1k ?? null };
          }
        }
        setPricing(stored);
        console.warn('Failed to load API pricing, using stored pricing only');
      }

      // Load snippets (non-blocking)
      try {
        const snippetResult = await api.listPrompts({ prompt_type: 'snippet' });
        setSnippets(snippetResult.prompts || []);
      } catch {
        console.warn('Failed to load snippets');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to load model data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Model Registry Handlers ────────────────────────────
  const handleSearchModels = async () => {
    const query = searchQuery.trim();
    if (!query || query.length < 2) return;
    setSearching(true);
    setError(null);
    setSearchResults([]);
    try {
      const result = await api.searchBedrockModels(query);
      // Filter out models already in the registry
      const existingIds = new Set(models.map((m) => m.model_id));
      const filtered = (result.matches || []).filter((m) => !existingIds.has(m.model_id));
      setSearchResults(filtered);
      if (filtered.length === 0 && (result.matches || []).length > 0) {
        setError(`All ${result.matches.length} matching models are already in your registry`);
      } else if (filtered.length === 0) {
        setError(`No Bedrock models found matching "${query}"`);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to search models');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectAndAddModel = async (match: { model_id: string; model_name: string; input_cost_per_1k: number | null; output_cost_per_1k: number | null }) => {
    setAddingModel(match.model_id);
    setError(null);
    try {
      await api.updateModel(match.model_id, {
        display_name: match.model_name,
        enabled: true,
        input_cost_per_1k: match.input_cost_per_1k,
        output_cost_per_1k: match.output_cost_per_1k,
      });
      setSearchQuery('');
      setSearchResults([]);
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to add model');
    } finally {
      setAddingModel(null);
    }
  };

  const handleToggleEnabled = async (model: AIModel) => {
    setError(null);
    try {
      await api.updateModel(model.model_id, {
        display_name: model.display_name,
        enabled: !model.enabled,
      });
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to toggle model');
    }
  };

  const handleEditSave = async (model: AIModel) => {
    if (!editDisplayName.trim()) return;
    setError(null);
    try {
      await api.updateModel(model.model_id, {
        display_name: editDisplayName.trim(),
        enabled: model.enabled,
      });
      setEditingModel(null);
      setEditDisplayName('');
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update model');
    }
  };

  const handleDeleteModel = async (modelId: string) => {
    if (!confirm(`Delete model "${modelId}"? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.deleteModel(modelId);
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to delete model');
    }
  };

  const handleTestModel = async (modelId: string) => {
    setTestingModel(modelId);
    setTestResult(null);
    setError(null);
    try {
      const result = await api.testModel(modelId);
      setTestResult({ modelId, response: result.response });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to test model');
    } finally {
      setTestingModel(null);
    }
  };

  const handleSeedData = async () => {
    if (!confirm('This will seed the default AI models into the system. Continue?')) return;
    setSeeding(true);
    setError(null);
    try {
      await api.seedModels();
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to seed models');
    } finally {
      setSeeding(false);
    }
  };

  // ─── Refresh Pricing Handler ────────────────────────────
  const handleRefreshPricing = async () => {
    setRefreshingPricing(true);
    setError(null);
    setPricingDebug(null);
    try {
      const pricingResult = await api.getModelPricing(true);
      const apiPricing = pricingResult.pricing || {};
      // Merge: stored model pricing takes precedence over API pricing
      const merged: Record<string, ModelPricingEntry> = { ...apiPricing };
      for (const m of models) {
        if (m.input_cost_per_1k != null || m.output_cost_per_1k != null) {
          merged[m.model_id] = {
            model_id: m.model_id,
            input_cost_per_1k: m.input_cost_per_1k ?? apiPricing[m.model_id]?.input_cost_per_1k ?? null,
            output_cost_per_1k: m.output_cost_per_1k ?? apiPricing[m.model_id]?.output_cost_per_1k ?? null,
          };
        }
      }
      setPricing(merged);
      // Show debug info if there are unmatched models
      if (pricingResult.unmatched_models || pricingResult.available_pricing_names) {
        setPricingDebug({
          unmatched_models: pricingResult.unmatched_models,
          available_pricing_names: pricingResult.available_pricing_names,
        });
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to refresh pricing from AWS');
    } finally {
      setRefreshingPricing(false);
    }
  };

  // ─── Assignment Handlers ────────────────────────────────
  const handleAssignmentChange = async (functionId: string, modelId: string) => {
    setSavingAssignment(functionId);
    setError(null);
    try {
      await api.updateModelAssignment(functionId, { model_id: modelId });
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update assignment');
    } finally {
      setSavingAssignment(null);
    }
  };

  // ─── Model Pricing Handlers ─────────────────────────────
  const handlePricingSave = async (model: AIModel, field: 'input_cost_per_1k' | 'output_cost_per_1k', value: string) => {
    setError(null);
    try {
      const numVal = parseFloat(value);
      await api.updateModel(model.model_id, {
        display_name: model.display_name,
        enabled: model.enabled,
        [field]: isNaN(numVal) ? null : numVal,
      });
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update pricing');
    }
  };

  // ─── Cost Config Handlers ───────────────────────────────
  const handleSaveCostConfig = async (newConfig: CostConfig) => {
    setSavingCostConfig(true);
    setError(null);
    try {
      await api.updateModelAssignment('_cost_config', { ...newConfig });
      setCostConfig(newConfig);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to save cost config');
    } finally {
      setSavingCostConfig(false);
    }
  };

  const handleCostConfigChange = (field: keyof CostConfig, value: string) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) return;
    const newConfig = { ...costConfig, [field]: num };
    setCostConfig(newConfig);
    handleSaveCostConfig(newConfig);
  };

  // ─── Function Cost Field Handlers ───────────────────────
  const handleFunctionCostFieldSave = async (functionId: string, field: string, value: string) => {
    setSavingAssignment(functionId);
    setError(null);
    try {
      const data: Record<string, string | number> = {};
      if (field === 'ai_pct') {
        data.ai_pct = parseFloat(value) || 0;
      } else {
        data[field] = value;
      }
      await api.updateModelAssignment(functionId, data);
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update function cost field');
    } finally {
      setSavingAssignment(null);
      setEditingField(null);
    }
  };

  const startEditing = (functionId: string, field: string, currentValue: string) => {
    setEditingField({ functionId, field });
    setEditFieldValue(currentValue);
  };

  // ─── Prompt Editor Handlers ────────────────────────────
  const selectVersion = (version: any) => {
    setSelectedVersion(version);
    setEditingPrompt({ ...version });
    cleanSnapshotRef.current = JSON.stringify(version);
  };

  // Track "clean" state for dirty detection
  const cleanSnapshotRef = useRef<string | null>(null);

  const isPromptDirty = useCallback((): boolean => {
    if (!editingPrompt) return false;
    if (cleanSnapshotRef.current === null) return true; // new unsaved version
    return JSON.stringify(editingPrompt) !== cleanSnapshotRef.current;
  }, [editingPrompt]);

  const closePromptEditor = useCallback(() => {
    if (isPromptDirty()) {
      if (!window.confirm('You have unsaved changes. Close without saving?')) return;
    }
    setPromptEditorOpen(false);
    setPromptError(null);
  }, [isPromptDirty]);

  // ESC key closes prompt editor
  useEffect(() => {
    if (!promptEditorOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closePromptEditor();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [promptEditorOpen, closePromptEditor]);

  const incrementPatch = (version: string): string => {
    const parts = version.split('.');
    if (parts.length === 3) {
      parts[2] = String(parseInt(parts[2] || '0') + 1);
      return parts.join('.');
    }
    return version + '.1';
  };

  const openPromptEditor = async (functionId: string) => {
    const promptName = FUNCTION_TO_PROMPT[functionId];
    if (!promptName) return;
    // Clear stale data immediately so previous prompt doesn't flash
    setSelectedPromptName(promptName);
    setPromptVersions([]);
    setSelectedVersion(null);
    setEditingPrompt(null);
    setPromptEditorOpen(true);
    setPromptError(null);
    setPromptLoading(true);
    try {
      const data = await api.getPromptVersions(promptName);
      const versions = data.prompts || [];
      setPromptVersions(versions);
      const prodVersions = versions.filter((p: any) => p.prompt_state === 'prod');
      if (prodVersions.length > 0) {
        selectVersion(prodVersions[0]);
      } else if (versions.length > 0) {
        selectVersion(versions[0]);
      } else {
        setSelectedVersion(null);
        setEditingPrompt(null);
      }
    } catch (err: any) {
      setPromptError(err?.response?.data?.message || 'Failed to load prompt versions');
    } finally {
      setPromptLoading(false);
    }
  };

  const openPromptByName = async (promptName: string) => {
    // Clear stale data immediately so previous prompt doesn't flash
    setSelectedPromptName(promptName);
    setPromptVersions([]);
    setSelectedVersion(null);
    setEditingPrompt(null);
    setPromptEditorOpen(true);
    setPromptError(null);
    setPromptLoading(true);
    try {
      const data = await api.getPromptVersions(promptName);
      const versions = data.prompts || [];
      setPromptVersions(versions);
      const prodVersions = versions.filter((p: any) => p.prompt_state === 'prod');
      if (prodVersions.length > 0) {
        selectVersion(prodVersions[0]);
      } else if (versions.length > 0) {
        selectVersion(versions[0]);
      } else {
        setSelectedVersion(null);
        setEditingPrompt(null);
      }
    } catch (err: any) {
      setPromptError(err?.response?.data?.message || 'Failed to load prompt versions');
    } finally {
      setPromptLoading(false);
    }
  };

  const addNewVersion = () => {
    if (!editingPrompt) return;
    const clone = {
      ...editingPrompt,
      prompt_state: 'test',
      traffic_pct: '0',
      prompt_version: incrementPatch(editingPrompt.prompt_version || '1.0.0'),
    };
    setEditingPrompt(clone);
    setSelectedVersion(null);
    cleanSnapshotRef.current = null; // mark as unsaved new version
    setFlashNewVersion(true);
    setTimeout(() => setFlashNewVersion(false), 1200);
  };

  const savePrompt = async () => {
    if (!editingPrompt) return;

    let versionToSave = editingPrompt.prompt_version;
    if (autoIncrementPatch && selectedVersion) {
      versionToSave = incrementPatch(editingPrompt.prompt_version);
    }

    const existing = promptVersions.find(
      (p: any) => p.prompt_version === versionToSave && p.prompt_name === editingPrompt.prompt_name
    );
    if (existing && (!selectedVersion || selectedVersion.prompt_version !== versionToSave)) {
      setPromptError(`Version ${versionToSave} already exists for ${editingPrompt.prompt_name}`);
      return;
    }

    setPromptSaving(true);
    setPromptError(null);
    try {
      await api.updatePrompt(editingPrompt.prompt_name, {
        ...editingPrompt,
        prompt_version: versionToSave,
      });
      const data = await api.getPromptVersions(editingPrompt.prompt_name);
      setPromptVersions(data.prompts || []);
      const saved = (data.prompts || []).find((p: any) => p.prompt_version === versionToSave);
      if (saved) selectVersion(saved);
    } catch (err: any) {
      setPromptError(err?.response?.data?.message || 'Failed to save prompt');
    }
    setPromptSaving(false);
  };

  const filteredPromptVersions = useMemo(() => {
    return promptVersions.filter((p: any) => {
      if (promptFilter.type && p.prompt_type !== promptFilter.type) return false;
      if (promptFilter.state && p.prompt_state !== promptFilter.state) return false;
      return true;
    });
  }, [promptVersions, promptFilter]);

  const stateColor = (state: string): string => {
    switch (state) {
      case 'prod': return 'bg-green-100 text-green-700 border-green-300';
      case 'test': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'expired': return 'bg-gray-100 text-gray-500 border-gray-300';
      default: return 'bg-gray-100 text-gray-500 border-gray-300';
    }
  };

  // ─── Calculated Values ──────────────────────────────────
  const costPerJob = useMemo(() => {
    return assignments.reduce((sum, a) => sum + calcCostPerRequest(a, costConfig, pricing), 0);
  }, [assignments, costConfig, pricing]);

  const costPerUserPerMonth = useMemo(() => {
    return costPerJob * costConfig.jobs_per_month;
  }, [costPerJob, costConfig.jobs_per_month]);

  const formatCost = (cost: number | null | undefined): string => {
    if (cost === null || cost === undefined) return 'N/A';
    if (cost === 0) return '$0';
    return `$${cost.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')}`;
  };

  const formatCostShort = (cost: number): string => {
    if (cost === 0) return '$0';
    if (cost < 0.01) return `$${cost.toFixed(6)}`;
    return `$${cost.toFixed(4)}`;
  };

  const enabledModels = models.filter((m) => m.enabled);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Brain className="w-7 h-7 text-violet-500" />
          <h1 className="text-2xl font-bold text-gray-800">AI Configuration</h1>
        </div>
        <div className="flex items-center gap-2">
          {models.length === 0 && !loading && (
            <button
              onClick={handleSeedData}
              disabled={seeding}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Seed Models
            </button>
          )}
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && models.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : models.length === 0 ? (
        <div className="text-center py-20">
          <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">No Models Configured</h2>
          <p className="text-gray-500 mb-6">
            Click "Seed Models" to load the default Bedrock models, or add models manually below.
          </p>
        </div>
      ) : null}

      {/* Section A: Model Registry */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Available Models (Model Registry)</h2>
              <p className="text-sm text-gray-500 mt-1">Manage the AI models available for generation functions. Pricing is auto-fetched from AWS and cached — edit fields to override.</p>
            </div>
            <button
              onClick={handleRefreshPricing}
              disabled={refreshingPricing}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 disabled:opacity-50 transition-colors whitespace-nowrap"
              title="Force refresh pricing from AWS Pricing API"
            >
              {refreshingPricing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DollarSign className="w-3.5 h-3.5" />}
              Refresh Pricing
            </button>
          </div>
        </div>

        {/* Pricing Debug Info Banner */}
        {pricingDebug && pricingDebug.unmatched_models && pricingDebug.unmatched_models.length > 0 && (
          <div className="mx-5 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">
                  {pricingDebug.unmatched_models.length} registered model{pricingDebug.unmatched_models.length !== 1 ? 's' : ''} did not match AWS pricing
                </span>
              </div>
              <button onClick={() => setPricingDebug(null)} className="text-amber-400 hover:text-amber-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="text-xs text-amber-700 mt-1 space-y-0.5">
              {pricingDebug.unmatched_models.map((m) => (
                <div key={m.model_id}>
                  <code className="bg-amber-100 px-1 rounded">{m.display_name}</code>
                  <span className="text-amber-500 ml-1">({m.model_id})</span>
                </div>
              ))}
            </div>
            {pricingDebug.available_pricing_names && (
              <details className="mt-2">
                <summary className="text-xs text-amber-600 cursor-pointer hover:text-amber-800">
                  Show {pricingDebug.available_pricing_names.length} available AWS pricing names
                </summary>
                <div className="mt-1 text-xs text-amber-600 max-h-40 overflow-y-auto">
                  {pricingDebug.available_pricing_names.map((name) => (
                    <div key={name}><code className="bg-amber-100/50 px-1 rounded">{name}</code></div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Add Model Search */}
        <div className="p-4 bg-gray-50 border-b border-gray-100">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Search Bedrock Models</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchResults([]); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Type a model name or ID (e.g. claude, nova, llama)..."
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearchModels(); }}
              />
            </div>
            <button
              onClick={handleSearchModels}
              disabled={!searchQuery.trim() || searchQuery.trim().length < 2 || searching}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap"
              title="Search Bedrock for matching models"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add Model
            </button>
          </div>
          {/* Search Results — click to add */}
          {searchResults.length > 0 && (
            <div className="mt-3 bg-white border border-primary-200 rounded-lg max-h-64 overflow-y-auto">
              <div className="px-3 py-2 text-xs font-semibold text-primary-700 bg-primary-50 border-b border-primary-200 sticky top-0">
                Found {searchResults.length} available model{searchResults.length !== 1 ? 's' : ''} — click to add:
              </div>
              {searchResults.map((match) => (
                <button
                  key={match.model_id}
                  onClick={() => handleSelectAndAddModel(match)}
                  disabled={addingModel === match.model_id}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-primary-50 border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-3 disabled:opacity-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {addingModel === match.model_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-500 flex-shrink-0" />
                      ) : (
                        <Plus className="w-3.5 h-3.5 text-primary-400 flex-shrink-0" />
                      )}
                      <span className="font-medium text-gray-800">{match.model_name}</span>
                      <span className="text-gray-400 text-xs">({match.provider})</span>
                    </div>
                    <code className="text-xs text-gray-500 ml-5.5 block mt-0.5 truncate">{match.model_id}</code>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 text-xs font-mono text-gray-500">
                    <span title="Input cost per 1K tokens">
                      In: {match.input_cost_per_1k != null ? `$${match.input_cost_per_1k}` : 'N/A'}
                    </span>
                    <span title="Output cost per 1K tokens">
                      Out: {match.output_cost_per_1k != null ? `$${match.output_cost_per_1k}` : 'N/A'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Models Table */}
        {models.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Display Name</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Model ID (Bedrock)</th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase px-5 py-3">$/1K In</th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase px-5 py-3">$/1K Out</th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase px-5 py-3">Enabled</th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {models.map((model) => (
                  <tr key={model.model_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    {/* Display Name */}
                    <td className="px-5 py-3">
                      {editingModel === model.model_id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editDisplayName}
                            onChange={(e) => setEditDisplayName(e.target.value)}
                            className="flex-1 px-2 py-1 border border-primary-300 rounded text-sm focus:ring-2 focus:ring-primary-500"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleEditSave(model);
                              if (e.key === 'Escape') setEditingModel(null);
                            }}
                          />
                          <button
                            onClick={() => handleEditSave(model)}
                            className="p-1 text-green-600 hover:text-green-700"
                            title="Save"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingModel(null)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span
                          className="font-medium text-gray-800 cursor-pointer hover:text-primary-600"
                          onClick={() => {
                            setEditingModel(model.model_id);
                            setEditDisplayName(model.display_name);
                          }}
                          title="Click to edit"
                        >
                          {model.display_name}
                        </span>
                      )}
                    </td>

                    {/* Model ID */}
                    <td className="px-5 py-3">
                      <code className="text-sm text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                        {model.model_id}
                      </code>
                    </td>

                    {/* Pricing In */}
                    <td className="px-5 py-2 text-right">
                      <input
                        type="number"
                        step="0.000001"
                        defaultValue={pricing[model.model_id]?.input_cost_per_1k ?? ''}
                        onBlur={(e) => handlePricingSave(model, 'input_cost_per_1k', e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        className="w-24 px-2 py-1 border border-gray-200 rounded text-xs font-mono text-right focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="N/A"
                        title="Cost per 1K input tokens — editable"
                      />
                    </td>
                    {/* Pricing Out */}
                    <td className="px-5 py-2 text-right">
                      <input
                        type="number"
                        step="0.000001"
                        defaultValue={pricing[model.model_id]?.output_cost_per_1k ?? ''}
                        onBlur={(e) => handlePricingSave(model, 'output_cost_per_1k', e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        className="w-24 px-2 py-1 border border-gray-200 rounded text-xs font-mono text-right focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="N/A"
                        title="Cost per 1K output tokens — editable"
                      />
                    </td>

                    {/* Enabled Toggle */}
                    <td className="px-5 py-3 text-center">
                      <button
                        onClick={() => handleToggleEnabled(model)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                          model.enabled
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {model.enabled ? (
                          <>
                            <ToggleRight className="w-4 h-4" />
                            Enabled
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="w-4 h-4" />
                            Disabled
                          </>
                        )}
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleTestModel(model.model_id)}
                          disabled={testingModel === model.model_id}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 rounded-lg hover:bg-violet-100 disabled:opacity-50 transition-colors"
                          title="Test model with a joke prompt"
                        >
                          {testingModel === model.model_id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Play className="w-3.5 h-3.5" />
                          )}
                          Test
                        </button>
                        <button
                          onClick={() => {
                            setEditingModel(model.model_id);
                            setEditDisplayName(model.display_name);
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteModel(model.model_id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                          title="Delete model"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Test Result */}
      {testResult && (
        <div className="mb-6 bg-violet-50 border border-violet-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Play className="w-4 h-4 text-violet-600" />
              <span className="font-semibold text-violet-800 text-sm">
                Test Result: <code className="bg-violet-100 px-1.5 py-0.5 rounded">{testResult.modelId}</code>
              </span>
            </div>
            <button onClick={() => setTestResult(null)} className="text-violet-400 hover:text-violet-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-violet-700 whitespace-pre-wrap">{testResult.response}</p>
        </div>
      )}

      {/* Section B: Cost Assumptions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">Cost Assumptions</h2>
          <p className="text-sm text-gray-500 mt-1">
            Admin planning estimates for AI cost modeling. These are not operational — for estimation only.
          </p>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Sub-section 1: Assumed Tokens per Data Source */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Assumed Tokens per Data Source</h3>
            <p className="text-xs text-gray-400 mb-3">Define token variables used in function cost formulas below.</p>
            <div className="space-y-3">
              {([
                { key: 'job_desc' as const, label: '{job_desc}', desc: 'Job description tokens' },
                { key: 'resume' as const, label: '{resume}', desc: 'Full resume tokens' },
                { key: 'small_section' as const, label: '{small_section}', desc: 'Single section tokens' },
                { key: 'experience_section' as const, label: '{experience_section}', desc: 'Experience section tokens' },
                { key: 'resume_tags' as const, label: '{resume_tags}', desc: 'Resume tag summary tokens' },
                { key: 'job_tags' as const, label: '{job_tags}', desc: 'Job desc tag summary tokens' },
              ]).map(({ key, label, desc }) => (
                <div key={key} className="flex items-center gap-3">
                  <code className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded font-mono w-32 text-center">{label}</code>
                  <input
                    type="number"
                    value={costConfig[key]}
                    onChange={(e) => handleCostConfigChange(key, e.target.value)}
                    disabled={savingCostConfig}
                    className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm text-right font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                    min={0}
                  />
                  <span className="text-xs text-gray-400">{desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sub-section 2: Assumed Cost per User per Month */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Assumed Cost per User per Month</h3>
            <p className="text-xs text-gray-400 mb-3">Calculated from function assignments and model pricing.</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                <span className="text-sm text-gray-600">Final Cost per Job</span>
                <span className="font-mono text-sm font-semibold text-gray-800">{formatCostShort(costPerJob)}</span>
              </div>
              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                <span className="text-sm text-gray-600">Jobs per User per Month</span>
                <input
                  type="number"
                  value={costConfig.jobs_per_month}
                  onChange={(e) => handleCostConfigChange('jobs_per_month', e.target.value)}
                  disabled={savingCostConfig}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                  min={0}
                />
              </div>
              <div className="flex items-center justify-between py-2 px-3 bg-violet-50 rounded border border-violet-200">
                <span className="text-sm font-semibold text-violet-700">Final Cost per User per Month</span>
                <span className="font-mono text-sm font-bold text-violet-800">{formatCostShort(costPerUserPerMonth)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section C: Function-to-Model Assignments */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">AI Configuration</h2>
          <p className="text-sm text-gray-500 mt-1">
            Assign which AI model each generation function uses. Cost fields use token variables from Cost Assumptions above.
          </p>
        </div>

        {assignments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">AI Task</th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase px-3 py-3 w-20">% AI</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 py-3">Token In / Request</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 py-3">Token Out / Request</th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase px-3 py-3 w-28">Cost/Request</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 py-3 min-w-[200px]">Assigned Model</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => {
                  const costPerReq = calcCostPerRequest(assignment, costConfig, pricing);
                  return (
                    <tr key={assignment.function_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      {/* Function ID / AI Task */}
                      <td className="px-4 py-2">
                        {FUNCTION_TO_PROMPT[assignment.function_id] ? (
                          <code
                            className="text-xs font-medium text-blue-600 bg-gray-100 px-2 py-0.5 rounded cursor-pointer hover:underline hover:text-blue-800"
                            onClick={() => openPromptEditor(assignment.function_id)}
                            title={`Edit prompt: ${FUNCTION_TO_PROMPT[assignment.function_id]}`}
                          >
                            {assignment.function_id}
                          </code>
                        ) : (
                          <code className="text-xs font-medium text-gray-800 bg-gray-100 px-2 py-0.5 rounded">
                            {assignment.function_id}
                          </code>
                        )}
                      </td>

                      {/* % AI */}
                      <td className="px-3 py-2 text-center">
                        {editingField?.functionId === assignment.function_id && editingField?.field === 'ai_pct' ? (
                          <input
                            type="number"
                            value={editFieldValue}
                            onChange={(e) => setEditFieldValue(e.target.value)}
                            onBlur={() => handleFunctionCostFieldSave(assignment.function_id, 'ai_pct', editFieldValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleFunctionCostFieldSave(assignment.function_id, 'ai_pct', editFieldValue);
                              if (e.key === 'Escape') setEditingField(null);
                            }}
                            className="w-16 px-1 py-0.5 border border-primary-300 rounded text-xs text-center font-mono focus:ring-2 focus:ring-primary-500"
                            autoFocus
                            min={0}
                            max={100}
                          />
                        ) : (
                          <span
                            className="text-xs font-mono text-gray-700 cursor-pointer hover:text-primary-600 hover:bg-primary-50 px-2 py-0.5 rounded"
                            onClick={() => startEditing(assignment.function_id, 'ai_pct', String(assignment.ai_pct ?? 0))}
                            title="Click to edit"
                          >
                            {assignment.ai_pct ?? 0}%
                          </span>
                        )}
                      </td>

                      {/* Token In / Request */}
                      <td className="px-3 py-2">
                        {editingField?.functionId === assignment.function_id && editingField?.field === 'token_in_formula' ? (
                          <input
                            type="text"
                            value={editFieldValue}
                            onChange={(e) => setEditFieldValue(e.target.value)}
                            onBlur={() => handleFunctionCostFieldSave(assignment.function_id, 'token_in_formula', editFieldValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleFunctionCostFieldSave(assignment.function_id, 'token_in_formula', editFieldValue);
                              if (e.key === 'Escape') setEditingField(null);
                            }}
                            className="w-full px-2 py-0.5 border border-primary-300 rounded text-xs font-mono focus:ring-2 focus:ring-primary-500"
                            autoFocus
                          />
                        ) : (
                          <div
                            className="cursor-pointer hover:bg-primary-50 px-2 py-0.5 rounded"
                            onClick={() => startEditing(assignment.function_id, 'token_in_formula', assignment.token_in_formula || '')}
                            title="Click to edit"
                          >
                            <code className="text-xs font-mono text-amber-700">{assignment.token_in_formula || '—'}</code>
                            <span className="text-xs text-gray-400 ml-2">({resolveFormula(assignment.token_in_formula, costConfig)})</span>
                          </div>
                        )}
                      </td>

                      {/* Token Out / Request */}
                      <td className="px-3 py-2">
                        {editingField?.functionId === assignment.function_id && editingField?.field === 'token_out_formula' ? (
                          <input
                            type="text"
                            value={editFieldValue}
                            onChange={(e) => setEditFieldValue(e.target.value)}
                            onBlur={() => handleFunctionCostFieldSave(assignment.function_id, 'token_out_formula', editFieldValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleFunctionCostFieldSave(assignment.function_id, 'token_out_formula', editFieldValue);
                              if (e.key === 'Escape') setEditingField(null);
                            }}
                            className="w-full px-2 py-0.5 border border-primary-300 rounded text-xs font-mono focus:ring-2 focus:ring-primary-500"
                            autoFocus
                          />
                        ) : (
                          <div
                            className="cursor-pointer hover:bg-primary-50 px-2 py-0.5 rounded"
                            onClick={() => startEditing(assignment.function_id, 'token_out_formula', assignment.token_out_formula || '')}
                            title="Click to edit"
                          >
                            <code className="text-xs font-mono text-amber-700">{assignment.token_out_formula || '—'}</code>
                            <span className="text-xs text-gray-400 ml-2">({resolveFormula(assignment.token_out_formula, costConfig)})</span>
                          </div>
                        )}
                      </td>

                      {/* Cost/Request (calculated) */}
                      <td className="px-3 py-2 text-right">
                        <span className={`text-xs font-mono font-semibold ${costPerReq > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                          {formatCost(costPerReq)}
                        </span>
                      </td>

                      {/* Assigned Model */}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <select
                            value={assignment.model_id}
                            onChange={(e) => handleAssignmentChange(assignment.function_id, e.target.value)}
                            disabled={savingAssignment === assignment.function_id}
                            className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                          >
                            {!enabledModels.find((m) => m.model_id === assignment.model_id) && assignment.model_id && (
                              <option value={assignment.model_id}>
                                {models.find((m) => m.model_id === assignment.model_id)?.display_name || assignment.model_id} (disabled)
                              </option>
                            )}
                            {enabledModels.map((model) => (
                              <option key={model.model_id} value={model.model_id}>
                                {model.display_name}
                              </option>
                            ))}
                          </select>
                          {savingAssignment === assignment.function_id && (
                            <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          !loading && (
            <div className="p-8 text-center text-gray-500">
              <p>No function assignments found. Seed the models first to create default assignments.</p>
            </div>
          )
        )}
      </div>

      {/* ─── AI Snippets and Parameters ────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Info className="w-5 h-5 text-indigo-500" />
            AI Snippets and Parameters
          </h2>
          <p className="text-sm text-gray-500 mt-1">Reusable prompt snippets and parameter reference</p>
        </div>
        <div className="grid grid-cols-2 gap-0">
          {/* Left: Snippets */}
          <div className="border-r border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Snippets</h3>
            {snippets.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Snippet</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Placeholder</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(new Map(snippets.map(s => [s.prompt_name, s])).values()).map((snippet) => {
                    const placeholder = `{{${snippet.prompt_name.toUpperCase()}}}`;
                    return (
                      <tr key={snippet.prompt_name} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <button
                            onClick={() => openPromptByName(snippet.prompt_name)}
                            className="text-sm font-mono text-violet-700 hover:text-violet-900 bg-violet-50 hover:bg-violet-100 px-2 py-0.5 rounded transition-colors cursor-pointer"
                          >
                            {snippet.prompt_name}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-left">
                          <button
                            onClick={() => copyToClipboard(placeholder)}
                            className="inline-flex items-center gap-1.5 text-xs font-mono text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded transition-colors cursor-pointer"
                            title="Click to copy"
                          >
                            {copiedText === placeholder ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                            {placeholder}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-gray-400">No snippets found. Seed prompts to create defaults.</p>
            )}
          </div>

          {/* Right: Parameters Reference */}
          <div className="p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Parameters Reference</h3>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Placeholder</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Description</th>
                </tr>
              </thead>
              <tbody>
                {PROMPT_PARAMETERS.map((param) => (
                  <tr key={param.name} className="border-b border-gray-100">
                    <td className="px-3 py-2 text-left">
                      <button
                        onClick={() => copyToClipboard(param.name)}
                        className="inline-flex items-center gap-1.5 text-xs font-mono text-amber-600 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded transition-colors cursor-pointer"
                        title="Click to copy"
                      >
                        {copiedText === param.name ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                        {param.name}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600">{param.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ─── Prompt Editor Modal ──────────────────────────────── */}
      {promptEditorOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closePromptEditor}
          />
          {/* Panel */}
          <div className="relative bg-white rounded-xl shadow-2xl max-w-[90vw] w-full mx-4 max-h-[95vh] min-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-violet-500" />
                <h2 className="text-lg font-semibold text-gray-800">
                  Prompt Editor: <code className="bg-violet-50 text-violet-700 px-2 py-0.5 rounded text-sm">{selectedPromptName}</code>
                </h2>
              </div>
              <button
                onClick={closePromptEditor}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Error */}
            {promptError && (
              <div className="mx-5 mt-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm">{promptError}</span>
                </div>
                <button onClick={() => setPromptError(null)} className="text-red-400 hover:text-red-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Body */}
            <div className="flex flex-1 overflow-hidden">
              {/* Left Panel: Version Selector */}
              <div className="w-64 border-r border-gray-200 flex flex-col bg-gray-50">
                {/* Filters */}
                <div className="p-3 border-b border-gray-200 space-y-2">
                  <select
                    value={promptFilter.type}
                    onChange={(e) => setPromptFilter((f) => ({ ...f, type: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Types</option>
                    <option value="validate">validate</option>
                    <option value="structure">structure</option>
                    <option value="select">select</option>
                    <option value="generate">generate</option>
                    <option value="snippet">snippet</option>
                  </select>
                  <select
                    value={promptFilter.state}
                    onChange={(e) => setPromptFilter((f) => ({ ...f, state: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All States</option>
                    <option value="prod">prod</option>
                    <option value="test">test</option>
                    <option value="expired">expired</option>
                  </select>
                </div>

                {/* Version List */}
                <div className="flex-1 overflow-y-auto">
                  {promptLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    </div>
                  ) : filteredPromptVersions.length === 0 ? (
                    <div className="p-4 text-center text-xs text-gray-400">
                      No versions found
                    </div>
                  ) : (
                    filteredPromptVersions.map((v: any, idx: number) => (
                      <button
                        key={`${v.prompt_version}-${v.prompt_type}-${idx}`}
                        onClick={() => selectVersion(v)}
                        className={`w-full text-left px-3 py-2.5 border-b border-gray-200 hover:bg-white transition-colors ${
                          selectedVersion?.prompt_version === v.prompt_version && selectedVersion?.prompt_type === v.prompt_type
                            ? 'bg-white border-l-2 border-l-violet-500'
                            : ''
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-mono font-medium text-gray-800">v{v.prompt_version}</span>
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${stateColor(v.prompt_state)}`}>
                            {v.prompt_state}
                          </span>
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {v.prompt_type} | traffic: {v.traffic_pct != null ? `${Math.round(parseFloat(v.traffic_pct) * 100 * 10) / 10}%` : '—'}
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {/* Add New Button */}
                <div className="p-3 border-t border-gray-200">
                  <button
                    onClick={addNewVersion}
                    disabled={!editingPrompt}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 disabled:opacity-50 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add New Version
                  </button>
                </div>
              </div>

              {/* Right Panel: Edit Form */}
              <div className="flex-1 overflow-y-auto p-5">
                {editingPrompt ? (
                  <div className="space-y-4">
                    {/* Row 1: version, type, state, traffic */}
                    <div className="grid grid-cols-4 gap-4">
                      <div className={`rounded-lg transition-all duration-700 ${flashNewVersion ? 'ring-2 ring-violet-400 bg-violet-50' : ''}`}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Version</label>
                        <input
                          type="text"
                          value={editingPrompt.prompt_version || ''}
                          onChange={(e) => setEditingPrompt({ ...editingPrompt, prompt_version: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="1.0.0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                        <select
                          value={editingPrompt.prompt_type || ''}
                          onChange={(e) => setEditingPrompt({ ...editingPrompt, prompt_type: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">Select...</option>
                          <option value="validate">validate</option>
                          <option value="structure">structure</option>
                          <option value="select">select</option>
                          <option value="generate">generate</option>
                          <option value="snippet">snippet</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
                        <select
                          value={editingPrompt.prompt_state || ''}
                          onChange={(e) => setEditingPrompt({ ...editingPrompt, prompt_state: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="prod">prod</option>
                          <option value="test">test</option>
                          <option value="expired">expired</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Traffic %</label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={editingPrompt.traffic_pct != null && editingPrompt.traffic_pct !== ''
                              ? String(Math.round(parseFloat(String(editingPrompt.traffic_pct)) * 100 * 10) / 10)
                              : ''}
                            onChange={(e) => {
                              const pct = parseFloat(e.target.value);
                              if (isNaN(pct)) { setEditingPrompt({ ...editingPrompt, traffic_pct: '' }); return; }
                              const clamped = Math.min(100, Math.max(0, pct));
                              setEditingPrompt({ ...editingPrompt, traffic_pct: String(Math.round(clamped / 100 * 10000) / 10000) });
                            }}
                            className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0.0"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                        </div>
                      </div>
                    </div>

                    {/* Updated At (read-only) */}
                    {editingPrompt.updated_at && (
                      <div className="text-xs text-gray-400">
                        Last updated: {new Date(editingPrompt.updated_at).toLocaleString()}
                      </div>
                    )}

                    {/* Prompt Text */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Prompt Text</label>
                      <textarea
                        value={editingPrompt.prompt_text || ''}
                        onChange={(e) => setEditingPrompt({ ...editingPrompt, prompt_text: e.target.value })}
                        rows={20}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
                        placeholder="Enter prompt text..."
                      />
                    </div>

                    {/* Save Controls */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={autoIncrementPatch}
                          onChange={(e) => {
                            setAutoIncrementPatch(e.target.checked);
                            localStorage.setItem('admin_auto_increment_patch', String(e.target.checked));
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        Increment patch +1 during save
                      </label>
                      <div className="flex items-center gap-3">
                        {!selectedVersion && (
                          <span className="text-xs text-amber-600 font-medium">Unsaved new version</span>
                        )}
                        <button
                          onClick={savePrompt}
                          disabled={promptSaving}
                          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors text-sm font-medium"
                        >
                          {promptSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                    {promptLoading ? 'Loading...' : 'Select a version from the left panel'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
